import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import Cookies from 'js-cookie';

/**
 * Production line type definition
 */
export interface ProductionLine {
  id: string;
  label: string;
  lineType: 'beverages' | 'pc' | 'tc';
  timeZone: string;
}

/**
 * App state interface
 */
interface AppState {
  // User state
  userEmail: string | null;
  
  // Production line state
  selectedProductionLine: ProductionLine | null;
  availableProductionLines: ProductionLine[];
  
  // UI preferences
  refreshRate: number;
  
  // Actions
  setUserEmail: (email: string | null) => void;
  setSelectedProductionLine: (line: ProductionLine | null) => void;
  setAvailableProductionLines: (lines: ProductionLine[]) => void;
  setRefreshRate: (rate: number) => void;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState = {
  userEmail: null,
  selectedProductionLine: null,
  availableProductionLines: [],
  refreshRate: 60000, // 1 minute default
};

/**
 * Get initial production line from cookies
 */
function getInitialProductionLine(): ProductionLine | null {
  const stored = Cookies.get('selectedProductionLine');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Main app store using Zustand
 * Replaces the old Context-based store
 */
export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      ...initialState,
      selectedProductionLine: getInitialProductionLine(),

      setUserEmail: (email) => set({ userEmail: email }),

      setSelectedProductionLine: (line) => {
        // Also store in cookie for backward compatibility
        if (line) {
          Cookies.set('selectedProductionLine', JSON.stringify(line));
        } else {
          Cookies.remove('selectedProductionLine');
        }
        set({ selectedProductionLine: line });
      },

      setAvailableProductionLines: (lines) => set({ availableProductionLines: lines }),

      setRefreshRate: (rate) => set({ refreshRate: rate }),

      reset: () => {
        Cookies.remove('selectedProductionLine');
        set(initialState);
      },
    }),
    {
      name: 'wint-app-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        refreshRate: state.refreshRate,
      }),
    }
  )
);

/**
 * Selector hooks for common access patterns
 */
export const useSelectedProductionLine = () => 
  useAppStore((state) => state.selectedProductionLine);

export const useSelectedProductionLineId = () => 
  useAppStore((state) => state.selectedProductionLine?.id ?? null);

export const useUserEmail = () => 
  useAppStore((state) => state.userEmail);

export const useRefreshRate = () => 
  useAppStore((state) => state.refreshRate);

/**
 * Get production line ID helper (for backward compatibility)
 */
export function getProductionLineId(state: { selectedProductionLine: ProductionLine | null }): string | null {
  return state.selectedProductionLine?.id ?? null;
}

export default useAppStore;
